import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Clock, Search, X } from "lucide-react";
import { useAppSelector } from "@/hooks/useReduxHooks";
import { selectUser } from "@/store/slices/user.slice";
import { cn } from "@/utils/cn";
import { EMOJI_CATEGORIES, type EmojiCategoryId } from "../_lib/emoji-data";
import { searchEmojis } from "../_lib/emoji-search";
import { useFrequentEmojis } from "../_lib/emoji-usage";

type SectionId = EmojiCategoryId | "frequent";

/** Two rows of my most used. */
const FREQUENT_SHOWN = 16;

const ALL_EMOJIS: readonly string[] = EMOJI_CATEGORIES.flatMap((c) => c.emojis);

/**
 * A compact full emoji picker: a search box, a category bar over one
 * scrolling grid with a heading per category — "Frequently used" first once
 * I've used some. `selected` (my current reaction) is ringed.
 */
export const EmojiPicker = ({
  onPick,
  selected,
  className,
}: {
  onPick: (emoji: string) => void;
  selected?: string | null;
  className?: string;
}) => {
  const { t } = useTranslation("chat");
  const scrollRef = useRef<HTMLDivElement>(null);
  const myUserId = useAppSelector(selectUser).user?.id;
  // Only ones the picker offers: what the API accepts as a reaction.
  const frequent = useFrequentEmojis(myUserId, FREQUENT_SHOWN * 2)
    .filter((e) => ALL_EMOJIS.includes(e))
    .slice(0, FREQUENT_SHOWN);
  const [query, setQuery] = useState("");
  const results = useMemo(() => searchEmojis(query, ALL_EMOJIS), [query]);
  const searching = query.trim().length > 0;

  const sections: { id: SectionId; emojis: readonly string[] }[] = [
    ...(frequent.length ? [{ id: "frequent" as const, emojis: frequent }] : []),
    ...EMOJI_CATEGORIES,
  ];
  const [active, setActive] = useState<SectionId>(sections[0].id);
  const title = (id: SectionId) => t(`emoji.categories.${id}`);

  const jump = (id: SectionId) => {
    setActive(id);
    const root = scrollRef.current;
    const section = root?.querySelector<HTMLElement>(`[data-emoji-category="${id}"]`);
    if (root && section) root.scrollTo({ top: section.offsetTop });
  };

  // The category bar follows the scroll: the last heading scrolled past wins.
  const onScroll = () => {
    const root = scrollRef.current;
    if (!root || searching) return;
    let current: SectionId = sections[0].id;
    for (const s of sections) {
      const el = root.querySelector<HTMLElement>(`[data-emoji-category="${s.id}"]`);
      if (el && el.offsetTop <= root.scrollTop + 8) current = s.id;
    }
    if (current !== active) setActive(current);
  };

  const grid = (emojis: readonly string[]) => (
    <div className="grid grid-cols-8">
      {emojis.map((e) => (
        <button
          key={e}
          type="button"
          onClick={() => onPick(e)}
          aria-pressed={selected === e}
          className={cn(
            "flex aspect-square items-center justify-center rounded-sm text-xl transition-transform hover:scale-110 hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden",
            selected === e && "bg-primary-muted ring-1 ring-primary",
          )}
        >
          {e}
        </button>
      ))}
    </div>
  );

  return (
    <div className={cn("flex w-72 flex-col", className)}>
      <div className="relative mb-1 px-1">
        <Search className="pointer-events-none absolute start-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            // Esc clears the search first; the popover closes on the next one.
            if (e.key === "Escape" && query) {
              e.stopPropagation();
              setQuery("");
            }
          }}
          placeholder={t("emoji.search")}
          aria-label={t("emoji.search")}
          className="h-8 w-full rounded-md border border-border bg-background ps-7 pe-7 text-sm outline-hidden placeholder:text-muted-foreground focus:border-primary [&::-webkit-search-cancel-button]:hidden"
        />
        {query ? (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label={t("emoji.clear_search")}
            className="absolute end-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted"
          >
            <X className="size-3.5" />
          </button>
        ) : null}
      </div>

      {searching ? null : (
        <div role="tablist" className="flex items-center justify-between border-b border-border px-1 pb-1">
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={active === s.id}
              aria-label={title(s.id)}
              title={title(s.id)}
              onClick={() => jump(s.id)}
              className={cn(
                "flex size-8 items-center justify-center rounded-sm text-base transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden",
                active === s.id && "bg-muted",
              )}
            >
              {s.id === "frequent" ? (
                <Clock className="size-4 text-muted-foreground" aria-hidden />
              ) : (
                <span aria-hidden>{s.emojis[0]}</span>
              )}
            </button>
          ))}
        </div>
      )}

      <div ref={scrollRef} onScroll={onScroll} className="relative h-64 overflow-y-auto px-1 pt-1">
        {searching ? (
          results.length ? (
            <section aria-label={t("emoji.results")}>{grid(results)}</section>
          ) : (
            <p className="px-2 py-8 text-center text-sm text-muted-foreground">{t("emoji.no_results")}</p>
          )
        ) : (
          sections.map((s) => (
            <section key={s.id} data-emoji-category={s.id} aria-label={title(s.id)}>
              <h4 className="sticky top-0 z-[1] bg-surface px-1 py-1 text-[11px] font-semibold text-muted-foreground">
                {title(s.id)}
              </h4>
              {grid(s.emojis)}
            </section>
          ))
        )}
      </div>
    </div>
  );
};
